import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import type {
  UsageTimeSeriesResponse,
  EngageUsageResponse,
  VoiceUsageResponse,
  ExtensionUsageResponse,
} from '@/hooks/useAdminUsers'

const COLORS = {
  copper: '#c27c3e',
  sky: '#0ea5e9',
  emerald: '#10b981',
  purple: '#8b5cf6',
  red: '#ef4444',
  amber: '#f59e0b',
  teal: '#14b8a6',
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-warm-200 bg-white p-5">
      <h4 className="text-sm font-semibold text-midnight-800 mb-4">{title}</h4>
      <div className="h-[240px]">
        {children}
      </div>
    </div>
  )
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// =============================================================================
// LOGIN ACTIVITY CHART
// =============================================================================

export function LoginActivityChart({ data }: { data: UsageTimeSeriesResponse }) {
  return (
    <ChartCard title="Login Activity">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data.data_points}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#a3998e" />
          <YAxis tick={{ fontSize: 11 }} stroke="#a3998e" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e0db', fontSize: 12 }}
            labelFormatter={formatDate}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="logins" name="Successful" stroke={COLORS.emerald} strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="failed_logins" name="Failed" stroke={COLORS.red} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// =============================================================================
// API USAGE CHART
// =============================================================================

export function ApiUsageChart({ data }: { data: UsageTimeSeriesResponse }) {
  return (
    <ChartCard title="API Usage">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.data_points}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#a3998e" />
          <YAxis tick={{ fontSize: 11 }} stroke="#a3998e" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e0db', fontSize: 12 }}
            labelFormatter={formatDate}
          />
          <Bar dataKey="api_calls" name="API Calls" fill={COLORS.sky} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// =============================================================================
// ENGAGE USAGE CHART
// =============================================================================

export function EngageUsageChart({ data }: { data: EngageUsageResponse }) {
  return (
    <ChartCard title="Engage Activity">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.time_series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#a3998e" />
          <YAxis tick={{ fontSize: 11 }} stroke="#a3998e" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e0db', fontSize: 12 }}
            labelFormatter={formatDate}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="sessions" name="Sessions" fill={COLORS.copper} radius={[3, 3, 0, 0]} />
          <Bar dataKey="messages" name="Messages" fill={COLORS.amber} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// =============================================================================
// VOICE USAGE CHART
// =============================================================================

export function VoiceUsageChart({ data }: { data: VoiceUsageResponse }) {
  return (
    <ChartCard title="Voice Processing">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.time_series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#a3998e" />
          <YAxis tick={{ fontSize: 11 }} stroke="#a3998e" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e0db', fontSize: 12 }}
            labelFormatter={formatDate}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="requests" name="Requests" fill={COLORS.purple} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// =============================================================================
// EXTENSION USAGE CHART
// =============================================================================

export function ExtensionUsageChart({ data }: { data: ExtensionUsageResponse }) {
  return (
    <ChartCard title="Extension Activity">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.time_series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#a3998e" />
          <YAxis tick={{ fontSize: 11 }} stroke="#a3998e" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e0db', fontSize: 12 }}
            labelFormatter={formatDate}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="sessions" name="Sessions" fill={COLORS.sky} radius={[3, 3, 0, 0]} />
          <Bar dataKey="turns" name="Turns" fill={COLORS.copper} radius={[3, 3, 0, 0]} />
          <Bar dataKey="nudges" name="Nudges" fill={COLORS.purple} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// =============================================================================
// CONSUMER ACTIVITY CHART
// =============================================================================

export function ConsumerActivityChart({ data }: { data: UsageTimeSeriesResponse }) {
  return (
    <ChartCard title="Consumer Activity">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data.data_points}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#a3998e" />
          <YAxis tick={{ fontSize: 11 }} stroke="#a3998e" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e0db', fontSize: 12 }}
            labelFormatter={formatDate}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="consumer_utterances" name="Utterances" stroke={COLORS.teal} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}
